import React, { useState, useEffect, useRef } from "react";
import axios from "../api/axios";
import {
  AvForm,
  AvField,
} from "availity-reactstrap-validation";
import {
  Row,
  Col,
  Card,
  Container,
  CardBody,
  Button,
  Label,
} from "reactstrap";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import logoSvg from "../../assets/images/rdig_logo.jpg";
import ThankYou from "../../assets/images/thnx.gif";
import "../../assets/css/rentdigicare.css";

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / 1024 ** i).toFixed(dm))} ${sizes[i]}`;
};

const ADD_VENDOR_URL = "/vendor/register";
const GET_SPECIALTY_URL = "/vendorspeciality/get_speciality";
const GET_PROPERTY_URL = "/property/getCompanyProperties";
const UPLOAD_DOCUMENTS_URL = "/vendor/upload-documents";

const VendorApplication = () => {
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [address, setAddress] = useState("");
  const [specialities, setSpecialities] = useState([]);
  const [selectedSpecialities, setSelectedSpecialities] = useState([]);
  const [propertiesList, setPropertiesList] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [profileFile, setProfileFile] = useState(null);
  const [documents, setDocuments] = useState([]);

  // Get company ID
  const companyId = (() => {
    try {
      const auth = JSON.parse(localStorage.getItem("authUser"));
      return auth?.userData?.id || "";
    } catch {
      return "";
    }
  })();

  useEffect(() => {
    if (!companyId) {
      toast.error("Company ID not found. Please log in again.");
      return;
    }

    const fetchSpecialities = async () => {
      try {
        const res = await axios.get(`${GET_SPECIALTY_URL}?companyId=${companyId}`);
        const options = res.data.specialties.map((s) => ({
          label: s.specialty,
          value: s._id,
        }));
        setSpecialities(options);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load specialities");
      }
    };

    const fetchProperties = async () => {
      try {
        const res = await axios.get(`${GET_PROPERTY_URL}?companyID=${companyId}`);
        const options = res.data.properties.map((p) => ({
          label: p.title,
          value: p._id,
        }));
        setPropertiesList(options);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load properties");
      }
    };

    fetchSpecialities();
    fetchProperties();
  }, [companyId]);

  const handleProfileDrop = (acceptedFiles) => {
    if (acceptedFiles?.length) {
      const file = acceptedFiles[0];
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      });
      setProfileFile(file);
    }
  };

  const handleDocumentsDrop = (acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) => ({
      ...file,
      preview: URL.createObjectURL(file),
      formattedSize: formatBytes(file.size),
    }));
    setDocuments((prev) => [...prev, ...newFiles]);
  };

  const removeProfile = () => setProfileFile(null);
  const removeDocument = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleValidSubmit = async (event, values) => {
    if (!companyId) {
      toast.error("Company ID missing. Please log in again.");
      return;
    }

    if (!profileFile) {
      toast.error("Profile picture is required");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        contact_no: phone.trim(),
        agency_name: agencyName.trim(),
        address: address.trim(),
        specialties: selectedSpecialities,
        properties: selectedProperties,
        interestedCompanies: [companyId],
      };

      const res = await axios.post(ADD_VENDOR_URL, payload);
      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to register vendor");
      }

      const vendorId = res.data.addedVendor._id;

      // Upload profile picture
      if (profileFile) {
        const fd = new FormData();
        fd.append("file", profileFile);
        await axios.post(UPLOAD_DOCUMENTS_URL, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          params: { id: vendorId, type: "profile" },
        });
      }

      // Upload supporting documents
      if (documents.length > 0) {
        const fd = new FormData();
        documents.forEach((file) => fd.append("file", file));
        await axios.post(UPLOAD_DOCUMENTS_URL, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          params: { id: vendorId, type: "id_proof_documents" },
        });
      }

      toast.success("Application submitted successfully!");
      setFormSubmitted(true);

      setTimeout(() => {
        window.location.href = "/viewvendor";
      }, 4000);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Submission failed";
      toast.error(
        msg.includes("already exists")
          ? "Email / Phone / Agency already registered"
          : msg
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInvalidSubmit = () => {
    toast.warn("Please fill all required fields correctly");
  };

  if (formSubmitted) {
    return (
      <div className="bg-soft-light min-vh-100 d-flex align-items-center justify-content-center py-5">
        <Container className="text-center">
          <img src={ThankYou} alt="Thank You" style={{ maxWidth: 300 }} />
          <h2 className="mt-4 text-success">Thank You!</h2>
          <p className="lead">Your vendor application has been successfully submitted.</p>
          <p>Redirecting to vendor list shortly...</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-soft-light min-vh-100 py-5">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="shadow">
              <CardBody className="p-4 p-md-5">
                <div className="text-center mb-5">
                  <img src={logoSvg} alt="Logo" height="70" className="mb-3" />
                  <h3>Vendor Application</h3>
                  <p className="text-muted">Please fill in the information below</p>
                </div>

                <AvForm
                  ref={formRef}
                  onValidSubmit={handleValidSubmit}
                  onInvalidSubmit={handleInvalidSubmit}
                >
                  {/* ── Personal Details Section ── */}
                  <h5 className="mb-4 border-bottom pb-2">Personal Details</h5>
                  <Row className="mb-2">
                    <Col md={4}>
                      <AvField
                        name="firstName"
                        label="First Name *"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </Col>
                    <Col md={4}>
                      <AvField
                        name="lastName"
                        label="Last Name *"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </Col>
                    <Col md={4}>
                      <AvField
                        name="agencyName"
                        label="Agency Name *"
                        value={agencyName}
                        onChange={(e) => setAgencyName(e.target.value)}
                        required
                      />
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Col md={6}>
                      <AvField
                        name="email"
                        type="email"
                        label="Email *"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        validate={{
                          pattern: {
                            value: "^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]+$",
                            errorMessage: "Invalid email format",
                          },
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <AvField
                        name="phone"
                        type="tel"
                        label="Contact Number *"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                        required
                        minLength={10}
                        maxLength={15}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Col md={6}>
                      <Label>Category / Specialization *</Label>
                      <Select
                        isMulti
                        options={specialities}
                        value={specialities.filter((opt) =>
                          selectedSpecialities.includes(opt.value)
                        )}
                        onChange={(opts) =>
                          setSelectedSpecialities(opts?.map((o) => o.value) || [])
                        }
                        className="basic-multi-select"
                        classNamePrefix="select"
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Properties *</Label>
                      <Select
                        isMulti
                        options={propertiesList}
                        value={propertiesList.filter((opt) =>
                          selectedProperties.includes(opt.value)
                        )}
                        onChange={(opts) =>
                          setSelectedProperties(opts?.map((o) => o.value) || [])
                        }
                        className="basic-multi-select"
                        classNamePrefix="select"
                        required
                      />
                    </Col>
                  </Row>

                  <AvField
                    name="address"
                    type="textarea"
                    label="Address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    rows={3}
                  />

                  {/* ── Documents Section ── */}
                  <h5 className="mt-5 mb-4 border-bottom pb-2">Documents</h5>

                  <Row>
                    <Col xs={12}>
                      <Label className="form-label">Profile Picture *</Label>
                      <Dropzone onDrop={handleProfileDrop} multiple={false} accept="image/*">
                        {({ getRootProps, getInputProps }) => (
                          <div {...getRootProps()} className="dropzone border rounded p-4 text-center">
                            <input {...getInputProps()} />
                            <i className="bx bxs-cloud-upload display-4 text-muted d-block mb-3" />
                            <p>Drop profile picture here or click to browse</p>
                          </div>
                        )}
                      </Dropzone>

                      {profileFile && (
                        <div className="mt-3 p-3 border rounded bg-light d-flex align-items-center">
                          <img
                            src={profileFile.preview}
                            alt="Profile preview"
                            className="rounded me-3"
                            height={80}
                            width={80}
                            style={{ objectFit: "cover" }}
                          />
                          <div className="flex-grow-1">
                            <strong>{profileFile.name}</strong>
                            <div className="text-muted small">{profileFile.formattedSize}</div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger ms-3"
                            onClick={removeProfile}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </Col>
                  </Row>

                  <Row className="mt-5">
                    <Col xs={12}>
                      <Label className="form-label">
                        Supporting Documents (ID Proof, Certificates, etc.)
                      </Label>
                      <Dropzone onDrop={handleDocumentsDrop} accept=".pdf,.jpg,.jpeg,.png">
                        {({ getRootProps, getInputProps }) => (
                          <div {...getRootProps()} className="dropzone border rounded p-4 text-center">
                            <input {...getInputProps()} />
                            <i className="bx bxs-cloud-upload display-4 text-muted d-block mb-3" />
                            <p>Drop files here or click to select (PDF, JPG, PNG)</p>
                          </div>
                        )}
                      </Dropzone>

                      {documents.length > 0 && (
                        <div className="mt-3">
                          {documents.map((file, idx) => (
                            <div
                              key={idx}
                              className="d-flex align-items-center border rounded p-2 mb-2 bg-light"
                            >
                              <img
                                src={file.preview}
                                alt="preview"
                                height={50}
                                width={50}
                                className="rounded me-3"
                                style={{ objectFit: "cover" }}
                              />
                              <div className="flex-grow-1">
                                <div>{file.name}</div>
                                <small className="text-muted">{file.formattedSize}</small>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger ms-3"
                                onClick={() => removeDocument(idx)}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </Col>
                  </Row>

                  {/* Submit Button */}
                  <div className="mt-5 pt-4 border-top text-end">
                    <Button
                      type="submit"
                      color="success"
                      disabled={loading}
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Vendor Application"
                      )}
                    </Button>
                  </div>
                </AvForm>
              </CardBody>
            </Card>

            <div className="text-center mt-4">
              <Link to="/viewvendor" className="text-muted">
                ← Back to Vendor List
              </Link>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VendorApplication;